/**
 * Сервис для работы с предопределенными элементами модулей
 * Аналог ModuleCalculationService из backend
 */
class ModuleElementsService {
  constructor() {
    // Предопределенные наборы из backend (для КП)
    this.predefinedKits = {
      'shower': {
        lighting: { fixtures: 2 },        // LED Світильник 36W
        plumbing: {
          waterHeaters: 2, // Бойлер 200л
          showers: 6,      // Душова кабіна
          sinks: 4,        // Умивальник
          showerDrains: 2  // Душовий трап лінійний
        },
      },
      'toilet': {
        lighting: { fixtures: 2 },        // LED Світильник 36W
        plumbing: {
          sinks: 4,        // Умивальник
          toilets: 3,      // Унітаз
          urinals: 2,      // Пісуар
          partitions: 5    // Сантехнічна перегородка
        },
      },
      'residential_universal': {
        lighting: { fixtures: 2 },        // LED Світильник 36W
        plumbing: {
          toilets: 1,      // Унітаз
          waterHeaters: 1, // Бойлер 80л
          sinks: 1,        // Умивальник
          showerDrains: 1  // Душовий трап
        },
        furniture: {
          beds: 2,         // Ліжко металеве
          desks: 1,        // Стіл письмовий
          chairs: 1        // Стілець офісний
        },
      },
      'sanitary_combined': {
        lighting: { fixtures: 2 },        // LED Світильник 36W
        plumbing: {
          toilets: 2,      // Унітаз
          urinals: 2,      // Пісуар
          showers: 2,      // Душова кабіна
          waterHeaters: 1, // Бойлер 200л
          sinks: 4,        // Умивальник
          partitions: 4    // Перегородка сантехнічна
        },
      },
      'kitchen': {
        lighting: { fixtures: 2 },        // LED Світильник 36W
        plumbing: {
          sinks: 2,        // Умивальник
        },
        furniture: {
          desks: 4,        // Стіл кухоний
          chairs: 3,       // Плита електрична
          beds: 1,         // Холодильник
        },
        kitchenEquipment: {
          shelves: 2      // Стелаж металевий
        }
      },
      'laundry': {
        lighting: { fixtures: 2 },        // LED Світильник 36W
        plumbing: {
          waterHeaters: 2, // Бак для води 1000л
        },
        furniture: {
          desks: 3,        // Сушарка
          chairs: 4,      // Пральна машина
        },
        kitchenEquipment: {
          shelves: 2      // Стелаж металевий
        }
      },
      'living_plan': {
        lighting: { fixtures: 2 },        // LED Світильник 36W
        plumbing: {
          waterHeaters: 1, // Бойлер
          toilets: 1,      // Унітаз
          sinks: 1,        // Умивальник
          showers: 1       // Душова кабіна
        },
        furniture: {
          beds: 1          // Ліжко металеве
        }
      }
    };

    // Предопределенные элементы, которые автоматически размещаются на модулях
    this.autoPlacedElements = {
      'shower': {
        door: 1,
        switch_double: 2,
        outlet_double: 2
      },
      'residential_universal': {
        // Двери не размещаются автоматически, так как они уже есть в SVG-плане
      },
      'toilet': {},
      'sanitary_combined': {},
      'living_plan': {
        // Двери не размещаются автоматически, так как они уже есть в SVG-плане
      }
    };
  }

  /**
   * Возвращает предопределенный набор оборудования для специализированных модулей.
   * @param {string} subtype - Подтип модуля.
   * @returns {object|null} - Объект с количеством элементов или null.
   */
  getPredefinedKit(subtype) {
    return this.predefinedKits[subtype] || null;
  }

  /**
   * Возвращает описание элементов в читаемом формате
   * @param {string} subtype - Подтип модуля.
   * @returns {string} - Описание элементов.
   */
  getElementsDescription(subtype) {
    const kit = this.getPredefinedKit(subtype);
    if (!kit) return '';

    const elementDescriptions = {
      lighting: 'LED Світильник 36W',
      plumbing: {
        waterHeaters: 'Бойлер',
        showers: 'Душова кабіна',
        sinks: 'Умивальник',
        showerDrains: 'Душовий трап',
        toilets: 'Унітаз',
        urinals: 'Пісуар',
        partitions: 'Сантехнічна перегородка'
      },
      furniture: {
        beds: 'Ліжко металеве 1900х800',
        desks: 'Стіл письмовий',
        chairs: 'Стілець офісний'
      },
      kitchenEquipment: {
        shelves: 'Стелаж металевий'
      }
    };
    
    // Специальные описания для разных модулей
    const specialDescriptions = {
      kitchen: {
        furniture: {
          desks: 'Стіл кухоний',
          chairs: 'Плита електрична',
          beds: 'Холодильник'
        }
      },
      laundry: {
        plumbing: {
          waterHeaters: 'Бак для води 1000л'
        },
        furniture: {
          desks: 'Сушарка',
          chairs: 'Пральна машина'
        }
      }
    };

    const parts = [];
    const special = specialDescriptions[subtype] || {};

    // Освещение
    if (kit.lighting?.fixtures) {
      parts.push(`${elementDescriptions.lighting} – ${kit.lighting.fixtures} шт.`);
    }

    // Сантехника
    if (kit.plumbing) {
      const plumbingItems = [];
      Object.entries(kit.plumbing).forEach(([key, count]) => {
        if (count > 0) {
          const description = special.plumbing?.[key] || elementDescriptions.plumbing[key];
          if (description) {
            plumbingItems.push(`${description} – ${count} шт.`);
          }
        }
      });
      if (plumbingItems.length > 0) {
        parts.push(...plumbingItems);
      }
    }

    // Мебель
    if (kit.furniture) {
      const furnitureItems = [];
      Object.entries(kit.furniture).forEach(([key, count]) => {
        if (count > 0) {
          const description = special.furniture?.[key] || elementDescriptions.furniture[key];
          if (description) {
            furnitureItems.push(`${description} – ${count} шт.`);
          }
        }
      });
      if (furnitureItems.length > 0) {
        parts.push(...furnitureItems);
      }
    }

    // Кухонное оборудование (стелажи)
    if (kit.kitchenEquipment) {
      const equipmentItems = [];
      Object.entries(kit.kitchenEquipment).forEach(([key, count]) => {
        if (count > 0 && elementDescriptions.kitchenEquipment[key]) {
          equipmentItems.push(`${elementDescriptions.kitchenEquipment[key]} – ${count} шт.`);
        }
      });
      if (equipmentItems.length > 0) {
        parts.push(...equipmentItems);
      }
    }

    return parts.length > 0 ? parts.join(', ') : '';
  }

  /**
   * Возвращает автоматически размещаемые элементы для модуля
   * @param {string} subtype - Подтип модуля.
   * @returns {object} - Объект с количеством автоматически размещаемых элементов.
   */
  getAutoPlacedElements(subtype) {
    return this.autoPlacedElements[subtype] || {};
  }

  /**
   * Возвращает полное описание всех элементов модуля (для tooltip)
   * @param {string} subtype - Подтип модуля.
   * @returns {string} - Полное описание элементов.
   */
  getCompleteElementsDescription(subtype) {
    const kitDescription = this.getElementsDescription(subtype);
    const autoElements = this.getAutoPlacedElements(subtype);

    const elementDescriptions = {
      door: 'Двері',
      switch_double: 'Подвійний вимикач',
      outlet_double: 'Подвійна розетка'
    };

    const autoParts = [];
    Object.entries(autoElements).forEach(([type, count]) => {
      if (count > 0 && elementDescriptions[type]) {
        autoParts.push(`${elementDescriptions[type]} – ${count} шт.`);
      }
    });

    const parts = [];

    // Добавляем элементы из предопределенного набора (для КП)
    if (kitDescription) {
      parts.push(`Предвстановлені (КП): ${kitDescription}`);
    }

    // Добавляем автоматически размещаемые элементы
    if (autoParts.length > 0) {
      parts.push(`Автоматично розміщені: ${autoParts.join(', ')}`);
    }

    return parts.length > 0 ? parts.join('\n') : '';
  }

  /**
   * Проверяет, есть ли у модуля предопределенные элементы
   * @param {string} subtype - Подтип модуля.
   * @returns {boolean} - true если есть предопределенные элементы.
   */
  hasPredefinedElements(subtype) {
    const kit = this.getPredefinedKit(subtype);
    return kit !== null;
  }

  /**
   * Проверяет, есть ли у модуля автоматически размещаемые элементы
   * @param {string} subtype - Подтип модуля.
   * @returns {boolean} - true если есть автоматически размещаемые элементы.
   */
  hasAutoPlacedElements(subtype) {
    const autoElements = this.getAutoPlacedElements(subtype);
    return Object.keys(autoElements).length > 0;
  }

  /**
   * Проверяет, есть ли у модуля какие-либо элементы
   * @param {string} subtype - Подтип модуля.
   * @returns {boolean} - true если есть какие-либо элементы.
   */
  hasAnyElements(subtype) {
    return this.hasPredefinedElements(subtype) || this.hasAutoPlacedElements(subtype);
  }
}

// Экспортируем singleton
const moduleElementsService = new ModuleElementsService();
export default moduleElementsService;
